import type { VariantProps } from '@joyful/utils'
import { cn, tv } from '@joyful/utils'
import type { ComponentProps, ReactNode } from 'react'
import { createContext, use } from 'react'

import { Copyable } from './Copyable'

const dataFieldListVariants = tv({
  base: 'grid',

  compoundVariants: [
    {
      className: 'gap-x-2 gap-y-2',
      direction: ['horizontal', 'inline'],
      size: 'lg',
    },

    {
      className: 'gap-x-2 gap-y-2',
      direction: ['horizontal', 'inline'],
      size: 'md',
    },

    {
      className: 'gap-x-2 gap-y-1.5',
      direction: ['horizontal', 'inline'],
      size: 'sm',
    },

    {
      className: 'gap-x-2 gap-y-2',
      direction: ['horizontal', 'inline'],
      size: 'xl',
    },

    {
      className: 'gap-x-2 gap-y-0.5',
      direction: 'vertical',
      size: 'md',
    },

    {
      className: 'grid-cols-[max-content_minmax(0,1fr)]',
      direction: 'horizontal',
      labelWidth: 'fit',
    },
  ],

  defaultVariants: {
    direction: 'horizontal',
    size: 'md',
  },

  variants: {
    direction: {
      horizontal: 'grid-cols-2',
      inline: 'grid-flow-col auto-cols-max',
      vertical: 'auto-rows-max grid-cols-1',
    },

    labelWidth: {
      fit: null,
    },

    size: {
      lg: 'text-base',
      md: 'text-sm',
      sm: 'text-xs',
      xl: 'text-lg',
    },
  },
})

type DataFieldContextValue = Omit<
  VariantProps<typeof dataFieldListVariants>,
  'labelWidth'
>

const DataFieldContext = createContext<DataFieldContextValue>(
  dataFieldListVariants.defaultVariants,
)

export type DataFieldItemProps = ComponentProps<'dl'> &
  VariantProps<typeof dataFieldListVariants>

export function DataFieldItem({
  children,
  className,
  direction,
  labelWidth,
  size,
  ...props
}: DataFieldItemProps) {
  const context = use(DataFieldContext)

  return (
    <DataFieldContext
      value={{
        direction: direction ?? context.direction,
        size: size ?? context.size,
      }}
    >
      <dl
        {...props}
        className={cn(
          dataFieldListVariants({
            direction: direction ?? context.direction,
            labelWidth,
            size: size ?? context.size,
          }),
          className,
        )}
      >
        {children}
      </dl>
    </DataFieldContext>
  )
}

export type DataFieldListProps = ComponentProps<typeof DataFieldItem>

export function DataFieldList({ children, ...props }: DataFieldListProps) {
  return <DataFieldItem {...props}>{children}</DataFieldItem>
}

export type DataFieldLabelProps = ComponentProps<'dt'>

export function DataFieldLabel({
  children,
  className,
  ...props
}: DataFieldLabelProps) {
  return (
    <dt
      {...props}
      className={cn(
        'text-tremor-content col-start-1 min-w-0 pt-0 pb-0 wrap-break-word',
        className,
      )}
    >
      {children}
    </dt>
  )
}

export type DataFieldValueProps = ComponentProps<'dd'>

export function DataFieldValue({
  children,
  className,
  ...props
}: DataFieldValueProps) {
  return (
    <dd
      {...props}
      className={cn(
        'text-tremor-content-strong min-w-0 wrap-break-word',
        className,
      )}
    >
      {typeof children === 'string' ? (
        <Copyable>{children}</Copyable>
      ) : (
        children
      )}
    </dd>
  )
}

interface DataFieldProps {
  children: ReactNode
  className?: string
  term: ReactNode
}

export function DataField({ children, className, term }: DataFieldProps) {
  return (
    <>
      {typeof term === 'string' ? (
        <DataFieldLabel>{term}</DataFieldLabel>
      ) : (
        term
      )}
      <DataFieldValue className={className}>{children}</DataFieldValue>
    </>
  )
}

export function DataFieldColumn({
  children,
  className,
  direction,
  size,
}: DataFieldContextValue & {
  children: ReactNode
  className?: string
}) {
  const context = use(DataFieldContext)
  return (
    <DataFieldContext
      value={{
        direction: direction ?? context.direction,
        size: size ?? context.size,
      }}
    >
      <div className={cn('flex flex-col gap-2', className)}>{children}</div>
    </DataFieldContext>
  )
}

export interface DataFieldRowListProps
  extends ComponentProps<'div'>, DataFieldContextValue {
  children: ReactNode
}

export interface DataFieldColumnListProps
  extends ComponentProps<'div'>, DataFieldContextValue {
  children: ReactNode
}

const dataFieldColumnListVariants = tv({
  base: 'flex flex-col gap-y-4 *:flex-[1_0_0] md:flex-row',
  variants: {
    size: {
      lg: '*:px-5 md:gap-x-5',
      md: '*:px-5 md:gap-x-5',
      sm: '*:pr-2 md:gap-x-3',
      xl: '*:px-5 md:gap-x-5',
    },
  },
})

export function DataFieldColumnList({
  children,
  className,
  direction,
  size,
}: DataFieldColumnListProps) {
  const context = use(DataFieldContext)
  return (
    <DataFieldContext
      value={{
        direction: direction ?? context.direction,
        size: size ?? context.size,
      }}
    >
      <div
        className={cn(
          dataFieldColumnListVariants({
            size: size ?? context.size,
          }),
          className,
        )}
      >
        {children}
      </div>
    </DataFieldContext>
  )
}

const dataFieldColumnTitleVariants = tv({
  base: 'font-semibold text-tremor-content-strong',
  variants: {
    size: {
      lg: 'text-lg',
      md: 'text-sm',
      sm: 'text-sm',
      xl: 'text-lg',
    },
  },
})

export function DataFieldColumnTitle({
  children,
  className,
  size,
  ...props
}: ComponentProps<'h4'> & DataFieldContextValue) {
  const context = use(DataFieldContext)
  return (
    <h4
      {...props}
      className={cn(
        dataFieldColumnTitleVariants({
          size: size ?? context.size,
        }),
        className,
      )}
    >
      {children}
    </h4>
  )
}
